import { useState, useRef, useEffect, useMemo, DragEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  User,
  ClipboardList,
  Target,
  Tag,
  AlertCircle,
  Loader2,
  Save,
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
  Upload,
  Eye,
  Download,
  X,
  Pencil,
  Home,
  TestTube2,
  FileDown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api, getDocumentUrl, downloadDocument } from '../../lib/api';
import { PostalCodeAutocomplete } from '../../components/PostalCodeAutocomplete';
import { useAuthStore } from '../../stores/auth-store';
import { Breadcrumb } from '../../components/ui';

// FormField component for inline validation
interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}

function FormField({ label, error, required, hint, children, className }: FormFieldProps) {
  return (
    <div className={className}>
      <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

// Enhanced Step Indicator component
interface StepIndicatorProps {
  steps: WizardStep[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (index: number) => void;
}

function StepIndicator({ steps, currentStep, completedSteps, onStepClick }: StepIndicatorProps) {
  return (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = completedSteps.has(index);
          const isCurrent = currentStep === index;
          const isPast = index < currentStep;
          const isClickable = isPast || isCompleted;

          return (
            <li key={step.id} className="relative flex-1">
              {/* Connecting line before */}
              {index > 0 && (
                <div
                  className={clsx(
                    'absolute top-5 left-0 right-1/2 h-0.5 -translate-y-1/2 transition-colors',
                    isPast || isCompleted ? 'bg-amber-500' : 'bg-gray-200'
                  )}
                />
              )}
              {/* Connecting line after */}
              {index < steps.length - 1 && (
                <div
                  className={clsx(
                    'absolute top-5 left-1/2 right-0 h-0.5 -translate-y-1/2 transition-colors',
                    isPast ? 'bg-amber-500' : 'bg-gray-200'
                  )}
                />
              )}

              <div className="relative flex flex-col items-center">
                <button
                  onClick={() => isClickable && onStepClick(index)}
                  disabled={!isClickable}
                  className={clsx(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white z-10 transition-all',
                    isCompleted && 'border-green-500 bg-green-500 cursor-pointer hover:bg-green-600',
                    isCurrent && !isCompleted && 'border-amber-500 ring-4 ring-amber-100',
                    !isCurrent && !isCompleted && isPast && 'border-amber-500 bg-amber-500 cursor-pointer hover:bg-amber-600',
                    !isCurrent && !isCompleted && !isPast && 'border-gray-300 cursor-not-allowed'
                  )}
                >
                  {isCompleted || isPast ? (
                    <Check className={clsx('h-5 w-5', isCompleted || isPast ? 'text-white' : 'text-amber-500')} />
                  ) : (
                    <Icon className={clsx('h-5 w-5', isCurrent ? 'text-amber-500' : 'text-gray-400')} />
                  )}
                </button>
                <span
                  className={clsx(
                    'mt-2 text-xs font-medium text-center whitespace-nowrap',
                    isCurrent ? 'text-amber-700' : isCompleted || isPast ? 'text-gray-700' : 'text-gray-500'
                  )}
                >
                  {step.name}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// Enhanced Document Upload component with drag-and-drop
interface DocumentUploadProps {
  uploadedFiles: UploadedFile[];
  existingDocuments: TestCaseDocument[];
  onFilesAdd: (files: File[]) => void;
  onRemoveUploadedFile: (id: string) => void;
  onDeleteDocument: (documentId: string) => void;
  onPreviewDocument: (doc: TestCaseDocument) => void;
  onPreviewLocalFile: (file: UploadedFile) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

function DocumentUpload({
  uploadedFiles,
  existingDocuments,
  onFilesAdd,
  onRemoveUploadedFile,
  onDeleteDocument,
  onPreviewDocument,
  onPreviewLocalFile,
  fileInputRef,
}: DocumentUploadProps) {
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFilesAdd(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    onFilesAdd(Array.from(files));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Documents</label>
        <span className="text-xs text-gray-400">Optional</span>
      </div>

      {/* Drag and drop area */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={clsx(
          'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
          isDragActive
            ? 'border-amber-500 bg-amber-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        )}
      >
        <Upload className={clsx('h-10 w-10 mx-auto mb-3', isDragActive ? 'text-amber-500' : 'text-gray-400')} />
        <p className="text-sm font-medium text-gray-700">
          {isDragActive ? 'Drop files here' : 'Drag & drop files, or click to browse'}
        </p>
        <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, JPG, PNG up to 10MB each</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
          className="hidden"
        />
      </div>

      {/* Existing Documents */}
      {existingDocuments.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Saved Documents</span>
          <ul className="mt-2 space-y-2">
            {existingDocuments.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                    {doc.originalName}
                  </span>
                  <span className="text-xs text-gray-400">{formatFileSize(doc.size)}</span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewDocument(doc);
                    }}
                    className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadDocument(doc.url, doc.originalName);
                    }}
                    className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteDocument(doc.id);
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Uploaded Files (not yet saved) */}
      {uploadedFiles.length > 0 && (
        <div>
          <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">
            New Files (will be saved with test case)
          </span>
          <ul className="mt-2 space-y-2">
            {uploadedFiles.map((uploadedFile) => (
              <li
                key={uploadedFile.id}
                className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg group"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-amber-500" />
                  <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                    {uploadedFile.file.name}
                  </span>
                  <span className="text-xs text-gray-400">{formatFileSize(uploadedFile.file.size)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {(uploadedFile.file.type.startsWith('image/') || uploadedFile.file.type === 'application/pdf') && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewLocalFile(uploadedFile);
                      }}
                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-100 rounded"
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveUploadedFile(uploadedFile.id);
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface WizardStep {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPS: WizardStep[] = [
  { id: 'basics', name: 'Basic Info', icon: FileText },
  { id: 'patient', name: 'Patient Info', icon: User },
  { id: 'questionnaire', name: 'Patient Questionnaire', icon: ClipboardList },
  { id: 'expected', name: 'Expected Outcomes', icon: Target },
  { id: 'review', name: 'Review & Save', icon: Check },
];

const CATEGORIES = [
  'ORTHOPEDIC',
  'CARDIAC',
  'NEUROLOGICAL',
  'ONCOLOGY',
  'GASTROENTEROLOGY',
  'PULMONOLOGY',
  'ENDOCRINE',
  'RHEUMATOLOGY',
  'DERMATOLOGY',
  'PSYCHIATRIC',
  'PEDIATRIC',
  'GERIATRIC',
  'OBSTETRIC',
  'UROLOGICAL',
  'OTHER',
];

const COMPLEXITIES = [
  { value: 'SIMPLE', label: 'Simple', description: 'Straightforward case with clear diagnosis' },
  { value: 'MODERATE', label: 'Moderate', description: 'Some complexity, multiple factors to consider' },
  { value: 'COMPLEX', label: 'Complex', description: 'Multiple conditions, nuanced decision required' },
  { value: 'EDGE_CASE', label: 'Edge Case', description: 'Unusual presentation, tests model boundaries' },
];

const QUESTION_TYPES = [
  { value: 'text', label: 'Text', description: 'Free text input' },
  { value: 'textarea', label: 'Text Area', description: 'Multi-line text' },
  { value: 'select', label: 'Single Select', description: 'Choose one option' },
  { value: 'multiselect', label: 'Multi Select', description: 'Choose multiple options' },
  { value: 'number', label: 'Number', description: 'Numeric input' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'boolean', label: 'Yes/No', description: 'Boolean choice' },
];

// Category short codes for code generation
const CATEGORY_SHORT_CODES: Record<string, string> = {
  ORTHOPEDIC: 'ORTHO',
  CARDIAC: 'CARD',
  NEUROLOGICAL: 'NEURO',
  ONCOLOGY: 'ONCO',
  GASTROENTEROLOGY: 'GASTRO',
  PULMONOLOGY: 'PULMO',
  ENDOCRINE: 'ENDO',
  RHEUMATOLOGY: 'RHEUM',
  DERMATOLOGY: 'DERM',
  PSYCHIATRIC: 'PSYCH',
  PEDIATRIC: 'PEDS',
  GERIATRIC: 'GERI',
  OBSTETRIC: 'OB',
  UROLOGICAL: 'URO',
  OTHER: 'GEN',
};

interface PatientInfo {
  age: number;
  gender: string;
  postalCode: string;
  canton: string;
  cityName: string;
  chiefComplaint: string;
  medicalHistory: string[];
  currentMedications: string[];
  allergies: string[];
}

interface Question {
  id: string;
  category: string;
  question: string;
  type: string;
  options?: string[];
  required: boolean;
}

interface QuestionAnswer {
  questionId: string;
  question: string;
  answer: string;
}

interface ExpectedOutcomes {
  suitability?: 'SUITABLE' | 'UNSUITABLE' | 'REQUIRES_REVIEW';
  confidenceRange?: { min: number; max: number };
  requiredIssues?: Array<{ severity: string; keywordMatch: string }>;
  requiredCriteria?: Array<{ field: string; valueMatch: string; priority: string }>;
}

interface UploadedFile {
  id: string;
  file: File;
}

interface TestCaseDocument {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
}

interface TestCaseData {
  id?: string;
  name: string;
  code: string;
  category: string;
  complexity: string;
  condition: string;
  description: string;
  tags: string[];
  patientInfo: PatientInfo;
  questionnaire: Question[];
  answers: QuestionAnswer[];
  expectedOutcomes: ExpectedOutcomes;
  isDraft: boolean;
  documents?: TestCaseDocument[];
  // Extra fields from API response (not sent to API)
  createdAt?: string;
  updatedAt?: string;
  createdBy?: unknown;
  _count?: unknown;
  version?: number;
  isActive?: boolean;
  validatedAt?: string;
  validatedBy?: string;
  validationNotes?: string;
  clinicalNotes?: string;
}

// Helper to extract only fields that should be sent to the API
function toApiPayload(data: TestCaseData): Omit<TestCaseData, 'id' | 'documents' | 'createdAt' | 'updatedAt' | 'createdBy' | '_count' | 'version' | 'validatedAt' | 'validatedBy' | 'validationNotes'> {
  const {
    id: _id,
    documents: _docs,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    createdBy: _createdBy,
    _count,
    version: _version,
    validatedAt: _validatedAt,
    validatedBy: _validatedBy,
    validationNotes: _validationNotes,
    ...payload
  } = data;
  return payload;
}

const initialData: TestCaseData = {
  name: '',
  code: '',
  category: 'ORTHOPEDIC',
  complexity: 'MODERATE',
  condition: '',
  description: '',
  tags: [],
  patientInfo: {
    age: 45,
    gender: 'male',
    postalCode: '',
    canton: '',
    cityName: '',
    chiefComplaint: '',
    medicalHistory: [],
    currentMedications: [],
    allergies: [],
  },
  questionnaire: [],
  answers: [],
  expectedOutcomes: {},
  isDraft: true,
};

export function TestCaseWizard() {
  const navigate = useNavigate();
  const { id: existingId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<TestCaseData>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [savedId, setSavedId] = useState<string | null>(existingId || null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [previewDocument, setPreviewDocument] = useState<TestCaseDocument | null>(null);
  const [previewLocalFile, setPreviewLocalFile] = useState<UploadedFile | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate unique code based on category
  const handleGenerateCode = async () => {
    setIsGeneratingCode(true);
    try {
      // Use API to get next available code for this category
      const response = await api.get<{ code: string }>(
        `/api/validation/test-cases/next-code/${data.category}`
      );
      if (response.code) {
        updateData('code', response.code);
      }
    } catch (error) {
      // Fallback to local generation if API fails
      const shortCode = CATEGORY_SHORT_CODES[data.category] || 'GEN';
      const timestamp = Date.now().toString().slice(-3);
      const fallbackCode = `${shortCode}-GEN-${timestamp}`;
      updateData('code', fallbackCode);
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Load existing test case if editing OR if we just saved a new one
  // This ensures documents uploaded during creation are fetched and displayed
  const testCaseIdToFetch = existingId || savedId;
  const { data: existingResponse } = useQuery({
    queryKey: ['validation', 'test-case', testCaseIdToFetch],
    queryFn: () => api.get<{ data: TestCaseData }>(`/api/validation/test-cases/${testCaseIdToFetch}`),
    enabled: !!testCaseIdToFetch,
  });

  useEffect(() => {
    if (existingResponse?.data) {
      setData(existingResponse.data);
      setSavedId(existingResponse.data.id || existingId || null);
    }
  }, [existingResponse, existingId]);

  const createMutation = useMutation({
    mutationFn: async (testCase: TestCaseData) => {
      const payload = toApiPayload(testCase);
      const result = await api.post<{ data: { id: string } }>('/api/validation/test-cases', payload);
      const newId = result.data.id;

      // Upload files if there are any
      if (uploadedFiles.length > 0 && newId) {
        const token = await useAuthStore.getState().getAccessToken();
        const formData = new FormData();
        uploadedFiles.forEach((f) => formData.append('files', f.file));
        await fetch(`/api/validation/test-cases/${newId}/documents`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      }

      return { id: newId };
    },
    onSuccess: (response) => {
      setSavedId(response.id);
      setLastSaved(new Date());
      setUploadedFiles([]);
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
      // Invalidate the test case query so documents are fetched and displayed
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-case', response.id] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (testCase: TestCaseData) => {
      const payload = toApiPayload(testCase);
      const result = await api.patch<{ data: { id: string } }>(`/api/validation/test-cases/${savedId}`, payload);

      // Upload new files if there are any
      if (uploadedFiles.length > 0 && savedId) {
        const token = await useAuthStore.getState().getAccessToken();
        const formData = new FormData();
        uploadedFiles.forEach((f) => formData.append('files', f.file));
        await fetch(`/api/validation/test-cases/${savedId}/documents`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      }

      return result;
    },
    onSuccess: () => {
      setLastSaved(new Date());
      setUploadedFiles([]);
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-case', savedId] });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!savedId) throw new Error('Test case not saved yet');
      await api.delete(`/api/validation/test-cases/${savedId}/documents/${documentId}`);
      return documentId;
    },
    onSuccess: (deletedId) => {
      // Remove the document from local state
      setData((prev) => ({
        ...prev,
        documents: prev.documents?.filter((doc) => doc.id !== deletedId),
      }));
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-case', savedId] });
    },
  });

  const handleDeleteDocument = (documentId: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      deleteDocumentMutation.mutate(documentId);
    }
  };

  const validateStep = (stepIndex: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepIndex === 0) {
      if (!data.name.trim()) newErrors.name = 'Name is required';
      if (!data.code.trim()) newErrors.code = 'Code is required';
      if (!data.condition.trim()) newErrors.condition = 'Condition is required';
    }

    if (stepIndex === 1) {
      if (!data.patientInfo.chiefComplaint.trim()) {
        newErrors.chiefComplaint = 'Chief complaint is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canSaveStep = (stepIndex: number): boolean => {
    // Step 0: Need name and code at minimum
    if (stepIndex === 0) {
      return data.name.trim() !== '' && data.code.trim() !== '' && data.condition.trim() !== '';
    }
    // Other steps: previous step must be saveable
    return canSaveStep(0);
  };

  const handleSaveStep = async () => {
    if (!canSaveStep(currentStep)) return;

    if (savedId) {
      updateMutation.mutate({ ...data, isDraft: true });
    } else {
      createMutation.mutate({ ...data, isDraft: true });
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleFinalSave = async (asDraft: boolean) => {
    if (!validateStep(currentStep)) return;

    const finalData = {
      ...data,
      isDraft: asDraft,
      isActive: !asDraft  // Set isActive to true when not saving as draft
    };

    if (savedId) {
      updateMutation.mutate(finalData, {
        onSuccess: async () => {
          // Remove stale cache data and refetch to ensure detail page sees fresh data
          // This prevents the detail page from seeing stale isDraft value and redirecting to edit
          queryClient.removeQueries({ queryKey: ['validation', 'test-case', savedId] });
          navigate(`/validation/test-cases/${savedId}`);
        },
      });
    } else {
      createMutation.mutate(finalData, {
        onSuccess: async (response) => {
          queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
          navigate(`/validation/test-cases/${response.id}`);
        },
      });
    }
  };

  const updateData = <K extends keyof TestCaseData>(key: K, value: TestCaseData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const updatePatientInfo = <K extends keyof PatientInfo>(key: K, value: PatientInfo[K]) => {
    setData((prev) => ({
      ...prev,
      patientInfo: { ...prev.patientInfo, [key]: value },
    }));
  };

  const removeUploadedFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleFilesAdd = (files: File[]) => {
    const newFiles = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  // Track completed steps
  const completedSteps = useMemo(() => {
    const completed = new Set<number>();
    // Step 0 is complete if all required fields are filled
    if (data.name.trim() && data.code.trim() && data.condition.trim()) {
      completed.add(0);
    }
    // Step 1 is complete if chief complaint is filled
    if (data.patientInfo.chiefComplaint.trim()) {
      completed.add(1);
    }
    // Steps 2-4 are optional, mark as complete if visited (past current step)
    for (let i = 2; i < currentStep; i++) {
      completed.add(i);
    }
    return completed;
  }, [data.name, data.code, data.condition, data.patientInfo.chiefComplaint, currentStep]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Model Validation', href: '/validation', icon: Home },
    { label: 'Test Cases', href: '/validation/test-cases', icon: TestTube2 },
    { label: savedId ? 'Edit Test Case' : 'New Test Case' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {savedId ? 'Edit Test Case' : 'Create Test Case'}
          </h1>
          <p className="text-sm text-gray-500">Define a gold-standard clinical scenario</p>
        </div>

        {/* Save Status */}
        <div className="flex items-center gap-3">
          {lastSaved && (
            <span className="text-xs text-gray-500">
              Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {canSaveStep(currentStep) && currentStep < STEPS.length - 1 && (
            <button
              onClick={handleSaveStep}
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Draft
            </button>
          )}
        </div>
      </div>

      {/* Enhanced Step Indicator */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={setCurrentStep}
        />
      </div>

      {/* Step Content */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        {currentStep === 0 && (
          <BasicInfoStep
            data={data}
            errors={errors}
            onUpdate={updateData}
            onGenerateCode={handleGenerateCode}
            isGeneratingCode={isGeneratingCode}
          />
        )}
        {currentStep === 1 && (
          <PatientInfoStep
            data={data.patientInfo}
            errors={errors}
            onUpdate={updatePatientInfo}
            uploadedFiles={uploadedFiles}
            existingDocuments={data.documents || []}
            onFilesAdd={handleFilesAdd}
            onRemoveUploadedFile={removeUploadedFile}
            onDeleteDocument={handleDeleteDocument}
            onPreviewDocument={setPreviewDocument}
            onPreviewLocalFile={setPreviewLocalFile}
            fileInputRef={fileInputRef}
          />
        )}
        {currentStep === 2 && (
          <QuestionnaireStep
            data={data}
            errors={errors}
            onUpdate={updateData}
          />
        )}
        {currentStep === 3 && (
          <ExpectedOutcomesStep
            data={data.expectedOutcomes}
            errors={errors}
            onUpdate={(outcomes) => updateData('expectedOutcomes', outcomes)}
          />
        )}
        {currentStep === 4 && (
          <ReviewStep data={data} uploadedFiles={uploadedFiles} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className={clsx(
            'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
            currentStep === 0
              ? 'cursor-not-allowed text-gray-400'
              : 'text-gray-700 hover:bg-gray-100'
          )}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          {currentStep === STEPS.length - 1 ? (
            <>
              <button
                onClick={() => handleFinalSave(true)}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Save as Draft
              </button>
              <button
                onClick={() => handleFinalSave(false)}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save & Activate
              </button>
            </>
          ) : (
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {(createMutation.isError || updateMutation.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Failed to save test case</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {(createMutation.error || updateMutation.error) instanceof Error
              ? (createMutation.error || updateMutation.error)?.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {/* Local File Preview Modal */}
      {previewLocalFile && (
        <LocalFilePreviewModal
          file={previewLocalFile.file}
          onClose={() => setPreviewLocalFile(null)}
        />
      )}
    </div>
  );
}

// Document Preview Modal
function DocumentPreviewModal({
  document,
  onClose,
}: {
  document: TestCaseDocument;
  onClose: () => void;
}) {
  const isImage = document.mimeType.startsWith('image/');
  const isPdf = document.mimeType === 'application/pdf';
  const fullUrl = getDocumentUrl(document.url);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {isImage ? '🖼️' : isPdf ? '📄' : '📎'}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {document.originalName}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadDocument(document.url, document.originalName)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {isImage ? (
            <img
              src={fullUrl}
              alt={document.originalName}
              className="max-w-full h-auto mx-auto"
            />
          ) : isPdf ? (
            <iframe
              src={fullUrl}
              className="w-full h-[70vh]"
              title={document.originalName}
            />
          ) : (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Preview not available for this file type</p>
              <button
                onClick={() => downloadDocument(document.url, document.originalName)}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
              >
                <Download className="h-4 w-4" />
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Local File Preview Modal
function LocalFilePreviewModal({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {isImage ? '🖼️' : isPdf ? '📄' : '📎'}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {file.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="max-w-full h-auto mx-auto"
            />
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-[70vh]"
              title={file.name}
            />
          ) : (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Preview not available for this file type
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Step 1: Basic Info with Documents
function BasicInfoStep({
  data,
  errors,
  onUpdate,
  onGenerateCode,
  isGeneratingCode,
}: {
  data: TestCaseData;
  errors: Record<string, string>;
  onUpdate: <K extends keyof TestCaseData>(key: K, value: TestCaseData[K]) => void;
  onGenerateCode: () => void;
  isGeneratingCode: boolean;
}) {
  const [tagInput, setTagInput] = useState('');

  const addTag = () => {
    if (tagInput.trim() && !data.tags.includes(tagInput.trim())) {
      onUpdate('tags', [...data.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    onUpdate('tags', data.tags.filter((t) => t !== tag));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
        <p className="text-sm text-gray-500">Define the test case metadata</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <FormField label="Name" required error={errors.name}>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onUpdate('name', e.target.value)}
            placeholder="e.g., Hip Replacement - Standard Case"
            className={clsx(
              'w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2',
              errors.name
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
            )}
          />
        </FormField>

        <FormField label="Code" required error={errors.code} hint="Format: CATEGORY-TYPE-NUMBER (e.g., ORTHO-HIP-001)">
          <div className="flex gap-2">
            <input
              type="text"
              value={data.code}
              onChange={(e) => onUpdate('code', e.target.value.toUpperCase())}
              placeholder="e.g., ORTHO-HIP-001"
              className={clsx(
                'flex-1 rounded-lg border bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2',
                errors.code
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
              )}
            />
            <button
              type="button"
              onClick={onGenerateCode}
              disabled={isGeneratingCode}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-50"
              title="Generate unique code based on category"
            >
              {isGeneratingCode ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Generate
            </button>
          </div>
        </FormField>

        <FormField label="Category" required>
          <select
            value={data.category}
            onChange={(e) => onUpdate('category', e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat.replace('_', ' ')}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Complexity" required>
          <select
            value={data.complexity}
            onChange={(e) => onUpdate('complexity', e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            {COMPLEXITIES.map((comp) => (
              <option key={comp.value} value={comp.value}>
                {comp.label} - {comp.description}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Medical Condition" required error={errors.condition} className="sm:col-span-2">
          <input
            type="text"
            value={data.condition}
            onChange={(e) => onUpdate('condition', e.target.value)}
            placeholder="e.g., Severe hip osteoarthritis requiring total hip replacement"
            className={clsx(
              'w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2',
              errors.condition
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
            )}
          />
        </FormField>

        <FormField label="Description" className="sm:col-span-2">
          <textarea
            value={data.description}
            onChange={(e) => onUpdate('description', e.target.value)}
            rows={3}
            placeholder="Describe the clinical scenario and what this test case evaluates..."
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          />
        </FormField>

        <FormField label="Tags" className="sm:col-span-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add tags..."
              className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
            <button
              type="button"
              onClick={addTag}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          {data.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-amber-600 hover:text-amber-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </FormField>
      </div>
    </div>
  );
}

// Step 2: Patient Info with ZIP autocomplete
function PatientInfoStep({
  data,
  errors,
  onUpdate,
  uploadedFiles,
  existingDocuments,
  onFilesAdd,
  onRemoveUploadedFile,
  onDeleteDocument,
  onPreviewDocument,
  onPreviewLocalFile,
  fileInputRef,
}: {
  data: PatientInfo;
  errors: Record<string, string>;
  onUpdate: <K extends keyof PatientInfo>(key: K, value: PatientInfo[K]) => void;
  uploadedFiles: UploadedFile[];
  existingDocuments: TestCaseDocument[];
  onFilesAdd: (files: File[]) => void;
  onRemoveUploadedFile: (id: string) => void;
  onDeleteDocument: (documentId: string) => void;
  onPreviewDocument: (doc: TestCaseDocument) => void;
  onPreviewLocalFile: (file: UploadedFile) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const [historyInput, setHistoryInput] = useState('');
  const [medicationInput, setMedicationInput] = useState('');
  const [allergyInput, setAllergyInput] = useState('');

  const addToList = (
    key: 'medicalHistory' | 'currentMedications' | 'allergies',
    value: string,
    setValue: (v: string) => void
  ) => {
    if (value.trim() && !data[key].includes(value.trim())) {
      onUpdate(key, [...data[key], value.trim()]);
      setValue('');
    }
  };

  const removeFromList = (
    key: 'medicalHistory' | 'currentMedications' | 'allergies',
    item: string
  ) => {
    onUpdate(key, data[key].filter((i) => i !== item));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Patient Information</h2>
        <p className="text-sm text-gray-500">Define the patient profile for this test case</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Age</label>
          <input
            type="number"
            min={0}
            max={120}
            value={data.age}
            onChange={(e) => onUpdate('age', parseInt(e.target.value) || 0)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Gender</label>
          <select
            value={data.gender}
            onChange={(e) => onUpdate('gender', e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Postal Code with Autocomplete */}
        <div className="sm:col-span-2 grid gap-4 sm:grid-cols-3">
          <div>
            <PostalCodeAutocomplete
              value={data.postalCode}
              onChange={(plz, canton, cityName) => {
                onUpdate('postalCode', plz);
                onUpdate('canton', canton);
                onUpdate('cityName', cityName);
              }}
              label="Postal Code"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Canton</label>
            <input
              type="text"
              value={data.canton}
              readOnly
              placeholder="Auto-filled"
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">City</label>
            <input
              type="text"
              value={data.cityName}
              readOnly
              placeholder="Auto-filled"
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Chief Complaint *</label>
          <textarea
            value={data.chiefComplaint}
            onChange={(e) => onUpdate('chiefComplaint', e.target.value)}
            rows={2}
            placeholder="Patient's primary reason for seeking care..."
            className={clsx(
              'mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2',
              errors.chiefComplaint
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
            )}
          />
          {errors.chiefComplaint && (
            <p className="mt-1 text-xs text-red-600">{errors.chiefComplaint}</p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Medical History</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              value={historyInput}
              onChange={(e) => setHistoryInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                (e.preventDefault(), addToList('medicalHistory', historyInput, setHistoryInput))
              }
              placeholder="Add condition..."
              className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
            <button
              type="button"
              onClick={() => addToList('medicalHistory', historyInput, setHistoryInput)}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          {data.medicalHistory.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.medicalHistory.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeFromList('medicalHistory', item)}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Current Medications</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              value={medicationInput}
              onChange={(e) => setMedicationInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                (e.preventDefault(),
                addToList('currentMedications', medicationInput, setMedicationInput))
              }
              placeholder="Add medication..."
              className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
            <button
              type="button"
              onClick={() =>
                addToList('currentMedications', medicationInput, setMedicationInput)
              }
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          {data.currentMedications.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.currentMedications.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeFromList('currentMedications', item)}
                    className="ml-1 text-blue-500 hover:text-blue-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Allergies</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              value={allergyInput}
              onChange={(e) => setAllergyInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                (e.preventDefault(), addToList('allergies', allergyInput, setAllergyInput))
              }
              placeholder="Add allergy..."
              className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
            <button
              type="button"
              onClick={() => addToList('allergies', allergyInput, setAllergyInput)}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          {data.allergies.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.allergies.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeFromList('allergies', item)}
                    className="ml-1 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Documents Section */}
        <div className="sm:col-span-2">
          <DocumentUpload
            uploadedFiles={uploadedFiles}
            existingDocuments={existingDocuments}
            onFilesAdd={onFilesAdd}
            onRemoveUploadedFile={onRemoveUploadedFile}
            onDeleteDocument={onDeleteDocument}
            onPreviewDocument={onPreviewDocument}
            onPreviewLocalFile={onPreviewLocalFile}
            fileInputRef={fileInputRef}
          />
        </div>
      </div>
    </div>
  );
}

// Step 3: Questionnaire with AI generation and question types
function QuestionnaireStep({
  data,
  errors,
  onUpdate,
}: {
  data: TestCaseData;
  errors: Record<string, string>;
  onUpdate: <K extends keyof TestCaseData>(key: K, value: TestCaseData[K]) => void;
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newQuestion, setNewQuestion] = useState<Question>({
    id: '',
    category: 'General',
    question: '',
    type: 'text',
    options: [],
    required: true,
  });
  const [newOptions, setNewOptions] = useState('');

  // PROM Import state
  const [showPromImport, setShowPromImport] = useState(false);
  const [promInstruments, setPromInstruments] = useState<
    Array<{ code: string; name: string; category: string; questionCount: number }>
  >([]);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [isLoadingProms, setIsLoadingProms] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [promPreview, setPromPreview] = useState<Question[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch PROM instruments when dialog opens
  useEffect(() => {
    if (showPromImport && promInstruments.length === 0) {
      setIsLoadingProms(true);
      api
        .get<{ instruments: Array<{ code: string; name: string; category: string; questionCount: number }> }>(
          '/api/validation/proms'
        )
        .then((data) => {
          if (data.instruments) {
            setPromInstruments(data.instruments);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoadingProms(false));
    }
  }, [showPromImport, promInstruments.length]);

  // Load preview when instrument or language changes
  useEffect(() => {
    if (selectedInstrument && showPromImport) {
      setIsLoadingProms(true);
      api
        .get<{ questions: Question[] }>(`/api/validation/proms/${selectedInstrument}/questions?lang=${selectedLanguage}`)
        .then((data) => {
          if (data.questions) {
            setPromPreview(data.questions);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoadingProms(false));
    } else {
      setPromPreview([]);
    }
  }, [selectedInstrument, selectedLanguage, showPromImport]);

  const importPromQuestions = () => {
    if (promPreview.length === 0) return;

    setIsImporting(true);
    try {
      // Add PROM questions to existing questionnaire
      const newQuestions = promPreview.map((q) => ({
        ...q,
        id: q.id || `prom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      }));

      onUpdate('questionnaire', [...data.questionnaire, ...newQuestions]);

      // Initialize answers for new questions
      const newAnswers: QuestionAnswer[] = newQuestions.map((q) => ({
        questionId: q.id,
        question: q.question,
        answer: '',
      }));
      onUpdate('answers', [...data.answers, ...newAnswers]);

      // Reset and close
      setShowPromImport(false);
      setSelectedInstrument('');
      setPromPreview([]);
      setShowPreview(false);
    } finally {
      setIsImporting(false);
    }
  };

  const generateQuestionnaire = async () => {
    if (!data.condition.trim()) {
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.andyPro.generateQuestionnaire({
        condition: data.condition,
        patientInfo: {
          age: data.patientInfo.age,
          gender: data.patientInfo.gender,
          canton: data.patientInfo.canton,
        },
      });

      if (response.success && response.data?.questions) {
        const questions: Question[] = response.data.questions.map((q) => ({
          id: q.id || `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          category: q.category || 'General',
          question: q.question,
          type: q.type || 'text',
          options: q.options,
          required: q.required ?? true,
        }));
        onUpdate('questionnaire', questions);

        // Initialize empty answers for each question
        const answers: QuestionAnswer[] = questions.map((q) => ({
          questionId: q.id,
          question: q.question,
          answer: '',
        }));
        onUpdate('answers', answers);
      }
    } catch (error) {
      console.error('Failed to generate questionnaire:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const autofillAnswers = async () => {
    if (data.questionnaire.length === 0 || !data.condition.trim()) {
      return;
    }

    setIsAutofilling(true);
    try {
      const response = await api.andyPro.autofill({
        condition: data.condition,
        patientInfo: {
          age: data.patientInfo.age,
          gender: data.patientInfo.gender,
          canton: data.patientInfo.canton,
        },
        questions: data.questionnaire.map((q) => ({
          id: q.id,
          question: q.question,
          type: q.type || 'text',
          options: q.options,
        })),
      });

      if (response.answers) {
        // Convert the answers object to our array format
        const updatedAnswers: QuestionAnswer[] = data.questionnaire.map((q) => ({
          questionId: q.id,
          question: q.question,
          answer: response.answers[q.id] || '',
        }));
        onUpdate('answers', updatedAnswers);
      }
    } catch (error) {
      console.error('Failed to autofill answers:', error);
    } finally {
      setIsAutofilling(false);
    }
  };

  const addQuestion = () => {
    if (!newQuestion.question.trim()) return;

    const question: Question = {
      ...newQuestion,
      id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      options: newOptions
        .split(',')
        .map((o) => o.trim())
        .filter((o) => o),
    };

    onUpdate('questionnaire', [...data.questionnaire, question]);
    onUpdate('answers', [
      ...data.answers,
      { questionId: question.id, question: question.question, answer: '' },
    ]);

    setNewQuestion({
      id: '',
      category: 'General',
      question: '',
      type: 'text',
      options: [],
      required: true,
    });
    setNewOptions('');
    setShowAddQuestion(false);
  };

  const removeQuestion = (index: number) => {
    const questionId = data.questionnaire[index]?.id;
    onUpdate(
      'questionnaire',
      data.questionnaire.filter((_, i) => i !== index)
    );
    onUpdate(
      'answers',
      data.answers.filter((a) => a.questionId !== questionId)
    );
  };

  const startEditQuestion = (index: number) => {
    const question = data.questionnaire[index];
    setNewQuestion({ ...question });
    setNewOptions(question.options?.join(', ') || '');
    setEditingIndex(index);
    setShowAddQuestion(false);
  };

  const saveEditQuestion = () => {
    if (editingIndex === null || !newQuestion.question.trim()) return;

    const updatedQuestion: Question = {
      ...newQuestion,
      options: newOptions
        .split(',')
        .map((o) => o.trim())
        .filter((o) => o),
    };

    const updatedQuestionnaire = [...data.questionnaire];
    updatedQuestionnaire[editingIndex] = updatedQuestion;
    onUpdate('questionnaire', updatedQuestionnaire);

    // Update the answer's question text if it changed
    const updatedAnswers = data.answers.map((a) =>
      a.questionId === updatedQuestion.id
        ? { ...a, question: updatedQuestion.question }
        : a
    );
    onUpdate('answers', updatedAnswers);

    cancelEdit();
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setNewQuestion({
      id: '',
      category: 'General',
      question: '',
      type: 'text',
      options: [],
      required: true,
    });
    setNewOptions('');
  };

  const updateAnswer = (questionId: string, answer: string) => {
    const updatedAnswers = data.answers.map((a) =>
      a.questionId === questionId ? { ...a, answer } : a
    );
    // If answer doesn't exist yet, add it
    if (!data.answers.find((a) => a.questionId === questionId)) {
      const question = data.questionnaire.find((q) => q.id === questionId);
      if (question) {
        updatedAnswers.push({ questionId, question: question.question, answer });
      }
    }
    onUpdate('answers', updatedAnswers);
  };

  const getAnswer = (questionId: string): string => {
    return data.answers.find((a) => a.questionId === questionId)?.answer || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Patient Questionnaire & Answers</h2>
          <p className="text-sm text-gray-500">
            Generate AI-powered questions or add your own
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={generateQuestionnaire}
            disabled={isGenerating || !data.condition.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2 text-sm font-medium text-white hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Generate with AI
          </button>
          <button
            type="button"
            onClick={() => setShowPromImport(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
          >
            <FileDown className="h-4 w-4" />
            Import PROM
          </button>
          <button
            type="button"
            onClick={() => setShowAddQuestion(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add Question
          </button>
        </div>
      </div>

      {/* PROM Import Dialog */}
      {showPromImport && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Import PROM Questionnaire</h3>
              <p className="text-xs text-gray-500">
                Import predefined Patient-Reported Outcome Measures
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowPromImport(false);
                setSelectedInstrument('');
                setPromPreview([]);
                setShowPreview(false);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instrument
              </label>
              <select
                value={selectedInstrument}
                onChange={(e) => setSelectedInstrument(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-none"
                style={{ backgroundImage: 'none' }}
                disabled={isLoadingProms}
              >
                <option value="">Select an instrument...</option>
                {promInstruments.map((inst) => (
                  <option key={inst.code} value={inst.code}>
                    {inst.code} - {inst.name} ({inst.questionCount} questions)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-none"
                style={{ backgroundImage: 'none' }}
              >
                <option value="de">German (Deutsch)</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          {/* Preview section */}
          {selectedInstrument && promPreview.length > 0 && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                {showPreview ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Preview ({promPreview.length} questions)
              </button>

              {showPreview && (
                <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                  {promPreview.slice(0, 10).map((q, idx) => (
                    <div key={q.id || idx} className="px-3 py-2">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-medium text-gray-400">{idx + 1}.</span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{q.question}</p>
                          <p className="text-xs text-gray-500">{q.category}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {promPreview.length > 10 && (
                    <div className="px-3 py-2 text-center text-xs text-gray-500">
                      ... and {promPreview.length - 10} more questions
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Import button */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowPromImport(false);
                setSelectedInstrument('');
                setPromPreview([]);
                setShowPreview(false);
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={importPromQuestions}
              disabled={!selectedInstrument || promPreview.length === 0 || isImporting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isImporting ? (
                <>
                  <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                  Importing...
                </>
              ) : (
                <>Import {promPreview.length} Questions</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* AI Auto-fill button - shown when there are questions */}
      {data.questionnaire.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800">
              Use AI to automatically fill in answers based on the condition and patient info
            </span>
          </div>
          <button
            type="button"
            onClick={autofillAnswers}
            disabled={isAutofilling || !data.condition.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isAutofilling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Auto-filling...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                AI Auto-fill Answers
              </>
            )}
          </button>
        </div>
      )}

      {/* Add Question Form */}
      {showAddQuestion && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Add New Question</h3>
            <button
              type="button"
              onClick={() => setShowAddQuestion(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Question *</label>
              <textarea
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                rows={2}
                placeholder="Enter your question..."
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <input
                type="text"
                value={newQuestion.category}
                onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                placeholder="e.g., Symptoms, History"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                value={newQuestion.type}
                onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label} - {t.description}
                  </option>
                ))}
              </select>
            </div>

            {(newQuestion.type === 'select' || newQuestion.type === 'multiselect') && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Options (comma-separated)
                </label>
                <input
                  type="text"
                  value={newOptions}
                  onChange={(e) => setNewOptions(e.target.value)}
                  placeholder="e.g., Yes, No, Sometimes"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>
            )}

            <div className="sm:col-span-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newQuestion.required}
                  onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                Required question
              </label>
              <button
                type="button"
                onClick={addQuestion}
                disabled={!newQuestion.question.trim()}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Add Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Form */}
      {editingIndex !== null && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Edit Question #{editingIndex + 1}
            </h3>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Question *</label>
              <textarea
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                rows={2}
                placeholder="Enter your question..."
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <input
                type="text"
                value={newQuestion.category}
                onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                placeholder="e.g., Symptoms, History"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                value={newQuestion.type}
                onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label} - {t.description}
                  </option>
                ))}
              </select>
            </div>

            {(newQuestion.type === 'select' || newQuestion.type === 'multiselect') && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Options (comma-separated)
                </label>
                <input
                  type="text"
                  value={newOptions}
                  onChange={(e) => setNewOptions(e.target.value)}
                  placeholder="e.g., Yes, No, Sometimes"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            )}

            <div className="sm:col-span-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newQuestion.required}
                  onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Required question
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEditQuestion}
                  disabled={!newQuestion.question.trim()}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errors.answers && (
        <p className="text-sm text-red-600">{errors.answers}</p>
      )}

      {/* Questions List */}
      {data.questionnaire.length > 0 ? (
        <div className="space-y-4">
          {data.questionnaire.map((question, index) => (
            <div
              key={question.id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 cursor-move text-gray-300 hover:text-gray-400">
                  <GripVertical className="h-5 w-5" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                          {index + 1}
                        </span>
                        <span className="text-xs text-gray-400">{question.category}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                          {QUESTION_TYPES.find((t) => t.value === question.type)?.label || question.type}
                        </span>
                        {question.required && (
                          <span className="text-xs text-red-500">Required</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900">{question.question}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEditQuestion(index)}
                        className="rounded p-1 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
                        title="Edit question"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeQuestion(index)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        title="Delete question"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Answer Input based on question type */}
                  <div>
                    <label className="block text-xs font-medium text-green-600 mb-1">Answer</label>
                    {question.type === 'select' && question.options ? (
                      <select
                        value={getAnswer(question.id)}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      >
                        <option value="">Select...</option>
                        {question.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : question.type === 'multiselect' && question.options ? (
                      <div className="flex flex-wrap gap-2">
                        {question.options.map((opt) => {
                          // Use ||| as delimiter to handle options with commas
                          const currentAnswers = getAnswer(question.id)
                            .split('|||')
                            .map((s) => s.trim())
                            .filter(Boolean);
                          const isSelected = currentAnswers.includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                // Recalculate current answers at click time to avoid stale closure values
                                const freshAnswers = getAnswer(question.id)
                                  .split('|||')
                                  .map((s) => s.trim())
                                  .filter(Boolean);
                                const wasSelected = freshAnswers.includes(opt);
                                const newAnswers = wasSelected
                                  ? freshAnswers.filter((a) => a !== opt)
                                  : [...freshAnswers, opt];
                                updateAnswer(question.id, newAnswers.join('|||'));
                              }}
                              className={clsx(
                                'px-3 py-1.5 text-sm rounded-lg border transition-colors',
                                isSelected
                                  ? 'bg-amber-100 border-amber-400 text-amber-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              )}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    ) : question.type === 'textarea' ? (
                      <textarea
                        value={getAnswer(question.id)}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        rows={3}
                        placeholder="Enter the patient's answer..."
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                    ) : question.type === 'boolean' ? (
                      <div className="flex gap-4">
                        {['Yes', 'No'].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => updateAnswer(question.id, opt)}
                            className={clsx(
                              'px-4 py-2 text-sm rounded-lg border transition-colors',
                              getAnswer(question.id) === opt
                                ? 'bg-amber-100 border-amber-400 text-amber-700'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            )}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : question.type === 'number' ? (
                      <input
                        type="number"
                        value={getAnswer(question.id)}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        placeholder="Enter a number..."
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                    ) : question.type === 'date' ? (
                      <input
                        type="date"
                        value={getAnswer(question.id)}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                    ) : (
                      <input
                        type="text"
                        value={getAnswer(question.id)}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        placeholder="Enter the patient's answer..."
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">
            No questions yet. Generate with AI or add questions manually.
          </p>
        </div>
      )}
    </div>
  );
}

// Step 4: Expected Outcomes
function ExpectedOutcomesStep({
  data,
  errors: _errors,
  onUpdate,
}: {
  data: ExpectedOutcomes;
  errors: Record<string, string>;
  onUpdate: (outcomes: ExpectedOutcomes) => void;
}) {
  const updateField = <K extends keyof ExpectedOutcomes>(key: K, value: ExpectedOutcomes[K]) => {
    onUpdate({ ...data, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Expected Outcomes</h2>
        <p className="text-sm text-gray-500">
          Define expected model outputs for auto-scoring (optional)
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Expected Suitability Decision
          </label>
          <select
            value={data.suitability || ''}
            onChange={(e) =>
              updateField(
                'suitability',
                (e.target.value as ExpectedOutcomes['suitability']) || undefined
              )
            }
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="">Not specified</option>
            <option value="SUITABLE">Suitable</option>
            <option value="UNSUITABLE">Unsuitable</option>
            <option value="REQUIRES_REVIEW">Requires Review</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Expected Confidence Range
          </label>
          <div className="mt-2 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Min:</span>
              <input
                type="number"
                min={0}
                max={100}
                value={data.confidenceRange?.min ?? ''}
                onChange={(e) =>
                  updateField('confidenceRange', {
                    min: parseInt(e.target.value) || 0,
                    max: data.confidenceRange?.max ?? 100,
                  })
                }
                placeholder="0"
                className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Max:</span>
              <input
                type="number"
                min={0}
                max={100}
                value={data.confidenceRange?.max ?? ''}
                onChange={(e) =>
                  updateField('confidenceRange', {
                    min: data.confidenceRange?.min ?? 0,
                    max: parseInt(e.target.value) || 100,
                  })
                }
                placeholder="100"
                className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Expected outcomes are used for auto-scoring during validation runs.
            Leave fields empty if you only want human evaluation.
          </p>
        </div>
      </div>
    </div>
  );
}

// Step 5: Review
function ReviewStep({ data, uploadedFiles }: { data: TestCaseData; uploadedFiles: UploadedFile[] }) {
  const totalDocuments = (data.documents?.length || 0) + uploadedFiles.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Review Test Case</h2>
        <p className="text-sm text-gray-500">Review all information before saving</p>
      </div>

      <div className="space-y-4">
        {/* Basic Info */}
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Basic Information
          </h3>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-gray-500">Name</dt>
              <dd className="text-sm font-medium text-gray-900">{data.name || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Code</dt>
              <dd className="font-mono text-sm text-gray-900">{data.code || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Category</dt>
              <dd className="text-sm text-gray-900">{data.category.replace('_', ' ')}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Complexity</dt>
              <dd className="text-sm text-gray-900">{data.complexity.replace('_', ' ')}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500">Condition</dt>
              <dd className="text-sm text-gray-900">{data.condition || '-'}</dd>
            </div>
            {totalDocuments > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Documents</dt>
                <dd className="text-sm text-gray-900">{totalDocuments} document(s) attached</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Patient Info */}
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Patient Information
          </h3>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-gray-500">Age</dt>
              <dd className="text-sm text-gray-900">{data.patientInfo.age} years</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Gender</dt>
              <dd className="text-sm text-gray-900">{data.patientInfo.gender}</dd>
            </div>
            {data.patientInfo.postalCode && (
              <div>
                <dt className="text-xs text-gray-500">Location</dt>
                <dd className="text-sm text-gray-900">
                  {data.patientInfo.postalCode} {data.patientInfo.cityName} ({data.patientInfo.canton})
                </dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500">Chief Complaint</dt>
              <dd className="text-sm text-gray-900">{data.patientInfo.chiefComplaint || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Q&A Summary */}
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Patient Questionnaire
          </h3>
          <p className="text-sm text-gray-700">
            {data.questionnaire.length} question{data.questionnaire.length !== 1 ? 's' : ''} defined
          </p>
          {data.answers.filter(a => a.answer).length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {data.answers.filter(a => a.answer).length} of {data.questionnaire.length} answered
            </p>
          )}
        </div>

        {/* Expected Outcomes */}
        {data.expectedOutcomes.suitability && (
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Expected Outcomes
            </h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-gray-500">Expected Suitability</dt>
                <dd className="text-sm text-gray-900">
                  {data.expectedOutcomes.suitability?.replace('_', ' ') || '-'}
                </dd>
              </div>
              {data.expectedOutcomes.confidenceRange && (
                <div>
                  <dt className="text-xs text-gray-500">Confidence Range</dt>
                  <dd className="text-sm text-gray-900">
                    {data.expectedOutcomes.confidenceRange.min}% -{' '}
                    {data.expectedOutcomes.confidenceRange.max}%
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
